import { DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import {
  CashflowDocumentListItem,
  FinanceApiService,
  FinancialAccountRecord
} from './finance-api.service';
import {
  PostingFilter,
  matchesAmountRange,
  matchesDateRange,
  matchesPostingFilter,
  matchesText,
  parseIsoDate,
  sumAmount
} from './finance-payments-report.utils';

@Component({
  standalone: true,
  selector: 'ain-finance-payments-report-payouts',
  imports: [
    AinPageComponent,
    TableModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    JalaliDateInputComponent,
    AinDatePipe,
    DecimalPipe
  ],
  template: `
    <ain-page [title]="t('finance.reports.payouts.title')" [description]="t('finance.reports.payouts.description')">
      <section class="card list-card">
        <div class="filters">
          <input pInputText type="text" [(ngModel)]="searchNumber" [placeholder]="t('finance.reports.filters.documentNumber')" />
          <input pInputText type="text" [(ngModel)]="searchAccount" [placeholder]="t('finance.reports.filters.account')" />
          <input pInputText type="text" [(ngModel)]="searchCurrency" [placeholder]="t('finance.reports.filters.currency')" />
          <ain-date-input [(ngModel)]="startDate" [placeholder]="t('finance.reports.filters.startDate')" />
          <ain-date-input [(ngModel)]="endDate" [placeholder]="t('finance.reports.filters.endDate')" />
          <input pInputText type="number" [(ngModel)]="minAmount" [placeholder]="t('finance.reports.filters.minAmount')" />
          <input pInputText type="number" [(ngModel)]="maxAmount" [placeholder]="t('finance.reports.filters.maxAmount')" />
          <p-select
            [options]="postingOptions"
            [(ngModel)]="postingFilter"
            optionLabel="label"
            optionValue="value"
            [placeholder]="t('finance.reports.filters.postingStatus')" />
          <button pButton type="button" [label]="t('finance.reports.apply')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('finance.reports.clear')" severity="secondary" (click)="clearFilters()"></button>
          <button pButton type="button" [label]="t('finance.reports.refresh')" severity="secondary" (click)="load()"></button>
        </div>

        <p-table [value]="filteredRows()" [paginator]="true" [rows]="15" [rowsPerPageOptions]="[15, 25, 50]">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('finance.reports.fields.documentNumber') }}</th>
              <th>{{ t('finance.reports.fields.documentDate') }}</th>
              <th>{{ t('finance.reports.fields.account') }}</th>
              <th>{{ t('finance.reports.fields.description') }}</th>
              <th>{{ t('finance.reports.fields.amount') }}</th>
              <th>{{ t('finance.reports.fields.currency') }}</th>
              <th>{{ t('finance.reports.fields.postingStatus') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.number }}</button></td>
              <td>{{ row.documentDateUtc | ainDate }}</td>
              <td>{{ accountLabel(row.accountingAccountId) }}</td>
              <td>{{ row.description || '-' }}</td>
              <td [class.negative]="row.amount < 0">{{ row.amount | number:'1.0-0' }}</td>
              <td>{{ row.currencyCode }}</td>
              <td>{{ postingLabel(row) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="7">{{ t('finance.reports.noRecords') }}</td></tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr class="totals-row">
              <td colspan="4"><strong>{{ t('finance.reports.totals.count') }}: {{ filteredRows().length }} — {{ t('finance.reports.totals.amount') }}</strong></td>
              <td colspan="3"><strong>{{ totalAmount() | number:'1.0-0' }}</strong></td>
            </tr>
          </ng-template>
        </p-table>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters p-select { min-width: 180px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .totals-row td { background: #f9fafb; }
    .negative { color: #b91c1c; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
  `]
})
export class FinancePaymentsReportPayoutsComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly rows = signal<CashflowDocumentListItem[]>([]);
  readonly filteredRows = signal<CashflowDocumentListItem[]>([]);
  readonly accounts = signal<FinancialAccountRecord[]>([]);
  readonly errorMessage = signal('');
  readonly totalAmount = computed(() => sumAmount(this.filteredRows()));

  searchNumber = '';
  searchAccount = '';
  searchCurrency = '';
  startDate = '';
  endDate = '';
  minAmount: number | null = null;
  maxAmount: number | null = null;
  postingFilter: PostingFilter = 'all';

  readonly postingOptions = [
    { value: 'all' as const, label: '' },
    { value: 'posted' as const, label: '' },
    { value: 'unposted' as const, label: '' }
  ];

  ngOnInit(): void {
    this.postingOptions[0].label = this.t('finance.reports.filters.postingAll');
    this.postingOptions[1].label = this.t('finance.reports.filters.postingPosted');
    this.postingOptions[2].label = this.t('finance.reports.filters.postingUnposted');
    this.loadReferenceData();
    this.load();
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listAccountingPaymentRecords({ page: 1, pageSize: 500 }).subscribe({
      next: data => {
        this.rows.set(data);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.errorMessage.set(this.t('finance.reports.payouts.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    const accountTerm = this.searchAccount.trim().toLowerCase();
    const startUtc = parseIsoDate(this.startDate);
    const endUtc = parseIsoDate(this.endDate, true);

    const filtered = this.rows().filter(row => {
      if (!matchesText(row.number, this.searchNumber)) {
        return false;
      }
      if (!matchesText(row.currencyCode, this.searchCurrency)) {
        return false;
      }
      if (!matchesDateRange(row.documentDateUtc, startUtc, endUtc)) {
        return false;
      }
      if (!matchesAmountRange(row.amount, this.minAmount, this.maxAmount)) {
        return false;
      }
      if (!matchesPostingFilter(row.journalEntryId, this.postingFilter)) {
        return false;
      }
      if (accountTerm) {
        const label = this.accountLabel(row.accountingAccountId).toLowerCase();
        if (!label.includes(accountTerm) && !row.accountingAccountId.toLowerCase().includes(accountTerm)) {
          return false;
        }
      }
      return true;
    });

    this.filteredRows.set(filtered);
  }

  clearFilters(): void {
    this.searchNumber = '';
    this.searchAccount = '';
    this.searchCurrency = '';
    this.startDate = '';
    this.endDate = '';
    this.minAmount = null;
    this.maxAmount = null;
    this.postingFilter = 'all';
    this.applyFilters();
  }

  openDetail(id: string): void {
    void this.router.navigate(['/financial/payments/payouts', id]);
  }

  accountLabel(accountId?: string): string {
    if (!accountId?.trim()) {
      return '-';
    }
    const account = this.accounts().find(item => item.id.toLowerCase() === accountId.toLowerCase());
    if (!account) {
      return '-';
    }
    return account.breadcrumb?.trim() || `${account.code} - ${account.name}`;
  }

  postingLabel(row: CashflowDocumentListItem): string {
    return row.journalEntryId
      ? this.t('finance.reports.posting.posted')
      : this.t('finance.reports.posting.unposted');
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadReferenceData(): void {
    forkJoin({
      accounts: this.api.listFinancialAccounts().pipe(catchError(() => of([])))
    }).subscribe({
      next: ({ accounts }) => this.accounts.set(accounts),
      error: () => this.accounts.set([])
    });
  }
}
